import { apiDelete, apiGet, apiPatch, apiPost } from './client'
import type { PartConstraint } from '../../entities/types/competition-constraints'

export type { PartConstraint }

export interface CompetitionOrganizer {
	id: string
	display_name: string
}

export interface CompetitionVessel {
	id: number
	name: string
	craft_file: string
	player: { id: string; display_name: string }
}

export interface Competition {
	id: number
	name: string
	description: string | null
	descriptionHtml: string
	status: 'accepting_submissions' | 'closed' | 'running' | 'completed'
	max_submissions: number
	remote_orchestration_password?: string
	organizers: CompetitionOrganizer[]
	vessels: CompetitionVessel[]
	isOrganizer: boolean
	created_at: string
	part_constraints: PartConstraint[] | null
	min_size_x: number | null
	max_size_x: number | null
	min_size_y: number | null
	max_size_y: number | null
	min_size_z: number | null
	max_size_z: number | null
	required_hull_material: string | null
}

export function listCompetitions(): Promise<Competition[]> {
	return apiGet('/api/competitions')
}

export function getCompetition(id: number | string): Promise<Competition> {
	return apiGet(`/api/competitions/${id}`)
}

export function createCompetition(name: string): Promise<Competition> {
	return apiPost('/api/competitions', { name })
}

export interface CompetitionSettings {
	name: string
	description: string
	remote_orchestration_password: string
	max_submissions: number
	part_constraints: PartConstraint[] | null
	min_size_x: number | null
	max_size_x: number | null
	min_size_y: number | null
	max_size_y: number | null
	min_size_z: number | null
	max_size_z: number | null
	required_hull_material: string | null
}

export function updateCompetition(
	id: number | string,
	data: CompetitionSettings,
): Promise<Competition> {
	return apiPatch(`/api/competitions/${id}`, data)
}

export function setCompetitionStage(
	id: number | string,
	stage: 'accepting_submissions' | 'closed',
): Promise<Competition> {
	return apiPost(`/api/competitions/${id}/stage`, { stage })
}

export async function validateVessel(
	competitionId: number | string,
	craftFile: File,
): Promise<{ errors: string[]; warnings: string[] }> {
	const form = new FormData()
	form.append('craft_file', craftFile)

	const res = await fetch(`/api/competitions/${competitionId}/validate-vessel`, {
		method: 'POST',
		body: form,
	})

	const data = await res.json()
	if (!data.success) {
		const errors = data.payload?.errors ?? [{ msg: String(data.payload) }]
		const { ApiError } = await import('./client')
		throw new ApiError(data.status, errors)
	}

	return data.payload
}

export async function submitVessel(
	competitionId: number | string,
	name: string,
	craftFile: File,
): Promise<{ warnings: string[] }> {
	const form = new FormData()
	form.append('name', name)
	form.append('craft_file', craftFile)

	const res = await fetch(`/api/competitions/${competitionId}/vessels`, {
		method: 'POST',
		body: form,
	})

	const data = await res.json()
	if (!data.success) {
		const errors = data.payload?.errors ?? [{ msg: String(data.payload) }]
		const { ApiError } = await import('./client')
		throw new ApiError(data.status, errors)
	}

	return data.payload
}

export function deleteVessel(competitionId: number | string, vesselId: number): Promise<void> {
	return apiDelete(`/api/competitions/${competitionId}/vessels/${vesselId}`)
}
